import { AnimatePresence, motion } from "framer-motion";
import { X, Radio, Film } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

function diffParts(target) {
  const now = new Date();
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff / 3600000) % 24);
  const minutes = Math.floor((diff / 60000) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds, expired: diff === 0 };
}

/**
 * Cinematic teaser lightbox — no external video.
 * Uses a custom canvas-like layered composition to fake a "trailer teaser card":
 *  - Cinemascope black bars top/bottom
 *  - Vertical film sprockets
 *  - Static noise + scanlines
 *  - Studio wordmark reveal
 *  - Countdown / release date
 *  - REC blinker
 */
export default function TrailerTeaserModal({ open, onClose, data }) {
  const target = useMemo(
    () => (data?.releaseISO ? new Date(data.releaseISO) : null),
    [data?.releaseISO]
  );
  const [t, setT] = useState(() => (target ? diffParts(target) : null));

  useEffect(() => {
    if (!open || !target) return;
    const id = setInterval(() => setT(diffParts(target)), 1000);
    return () => clearInterval(id);
  }, [open, target]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!data) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="trailer-teaser-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[400] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 md:p-10"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-6xl aspect-video bg-[#0B0B0F] border border-accent/25 overflow-hidden shadow-[0_0_160px_rgba(250,204,21,0.08)]"
          >
            {/* Close button + status (inside the frame, above cinemascope) */}
            <div className="absolute top-4 md:top-6 left-4 md:left-6 right-4 md:right-6 flex items-center justify-between z-30">
              <div className="flex items-center gap-3">
                <motion.span
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 1.4, repeat: Infinity }}
                  className="flex items-center gap-2"
                >
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="font-body text-[10px] uppercase tracking-[0.35em] text-red-400">
                    REC
                  </span>
                </motion.span>
                <span className="font-body text-[10px] uppercase tracking-[0.3em] text-white/40 hidden sm:inline">
                  {data.kicker}
                </span>
              </div>
              <button
                type="button"
                data-testid="close-trailer-teaser-btn"
                onClick={onClose}
                className="flex items-center gap-2 border border-white/20 hover:border-accent hover:text-accent text-white/70 px-3 py-2 backdrop-blur-md bg-black/60 transition-colors"
              >
                <span className="font-body text-[10px] uppercase tracking-[0.3em]">
                  Chiudi
                </span>
                <X size={14} />
              </button>
            </div>

            {/* Cinemascope bars */}
            <div className="pointer-events-none absolute top-0 left-0 right-0 h-[8%] bg-black z-20" />
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-[8%] bg-black z-20" />

            {/* Film sprockets (both sides) */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-6 z-10 flex flex-col justify-between py-4">
              {Array.from({ length: 14 }).map((_, i) => (
                <span
                  key={i}
                  className="mx-auto w-3 h-2.5 rounded-sm bg-white/[0.06]"
                />
              ))}
            </div>
            <div className="pointer-events-none absolute inset-y-0 right-0 w-6 z-10 flex flex-col justify-between py-4">
              {Array.from({ length: 14 }).map((_, i) => (
                <span
                  key={i}
                  className="mx-auto w-3 h-2.5 rounded-sm bg-white/[0.06]"
                />
              ))}
            </div>

            {/* Scanlines overlay */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 z-10 opacity-25"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(0deg, rgba(255,255,255,0.05) 0, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 3px)",
              }}
            />

            {/* Static noise */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 z-10 opacity-[0.08] mix-blend-overlay"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.9'/></svg>\")",
              }}
            />

            {/* Radial vignette */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(0,0,0,0.85)_95%)]"
            />

            {/* Content stack */}
            <div className="relative z-20 h-full w-full flex flex-col items-center justify-center px-10 md:px-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.9 }}
                className="flex items-center gap-3 mb-5 md:mb-8"
              >
                <Film size={14} className="text-accent" />
                <span className="font-body text-[10px] md:text-xs uppercase tracking-[0.4em] text-accent">
                  Ashvolt Studios presenta
                </span>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, letterSpacing: "0.5em" }}
                animate={{ opacity: 1, letterSpacing: "0.06em" }}
                transition={{ delay: 0.6, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                className="font-heading uppercase text-white text-4xl md:text-6xl lg:text-7xl leading-[0.9] text-center max-w-4xl"
              >
                {data.title}
              </motion.h2>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3, duration: 0.8 }}
                className="mt-4 md:mt-6 font-body text-xs md:text-sm uppercase tracking-[0.35em] text-white/50"
              >
                {data.subtitle}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.7, duration: 0.9 }}
                className="mt-8 md:mt-10 flex flex-col items-center gap-3"
              >
                {t && !t.expired ? (
                  <>
                    <div
                      data-testid="teaser-countdown"
                      className="flex items-center gap-4 md:gap-6 font-heading text-3xl md:text-5xl text-accent tabular-nums"
                    >
                      <span className="flex flex-col items-center">
                        <span>{String(t.days).padStart(2, "0")}</span>
                        <span className="font-body text-[9px] tracking-[0.3em] text-white/40 mt-1">
                          Giorni
                        </span>
                      </span>
                      <span className="text-accent/40">:</span>
                      <span className="flex flex-col items-center">
                        <span>{String(t.hours).padStart(2, "0")}</span>
                        <span className="font-body text-[9px] tracking-[0.3em] text-white/40 mt-1">
                          Ore
                        </span>
                      </span>
                      <span className="text-accent/40">:</span>
                      <span className="flex flex-col items-center">
                        <span>{String(t.minutes).padStart(2, "0")}</span>
                        <span className="font-body text-[9px] tracking-[0.3em] text-white/40 mt-1">
                          Min
                        </span>
                      </span>
                      <span className="text-accent/40 hidden md:inline">:</span>
                      <span className="hidden md:flex flex-col items-center">
                        <span>{String(t.seconds).padStart(2, "0")}</span>
                        <span className="font-body text-[9px] tracking-[0.3em] text-white/40 mt-1">
                          Sec
                        </span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <Radio size={12} className="text-accent" />
                      <span className="font-body text-xs md:text-sm uppercase tracking-[0.3em] text-white/70">
                        {data.releaseDate}
                      </span>
                    </div>
                  </>
                ) : (
                  <div
                    data-testid="teaser-live"
                    className="flex items-center gap-3 border border-accent/60 px-6 py-3"
                  >
                    <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                    <span className="font-heading text-xl uppercase tracking-[0.15em] text-accent">
                      Trailer disponibile — Guarda ora
                    </span>
                  </div>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.2, duration: 0.8 }}
                className="mt-8 md:mt-10 flex flex-wrap items-center justify-center gap-2 max-w-2xl"
              >
                {(data.channels || []).map((c) => (
                  <span
                    key={c}
                    className="font-body text-[9px] md:text-[10px] uppercase tracking-[0.3em] text-white/55 border border-white/15 px-3 py-1.5 bg-black/40 backdrop-blur-sm"
                  >
                    {c}
                  </span>
                ))}
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.6, duration: 0.8 }}
                className="mt-6 md:mt-8 font-body text-[10px] uppercase tracking-[0.3em] text-white/35 text-center max-w-lg"
              >
                {data.note}
              </motion.p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
